// src/pages/Admin/OnboardingDiagnosticsPanel.js
//
// Onboarding diagnostics for Admin → Activation (and the small summary on
// Admin → Onboarding, and the per-user timeline on Admin → Users). Everything
// is unique users; tiny cohorts are flagged and their rates de-emphasised.
import React, { useEffect, useState } from 'react';
import adminApi from '../../services/adminApi';
import styles from './OnboardingDiagnostics.module.css';

// ─── Filters ────────────────────────────────────────────────────────────────

export const FILTER_KEYS = [
    'platform', 'method', 'source', 'device_os', 'app_version', 'build_number',
    'os_version', 'manufacturer', 'model', 'install_type', 'onboarding_version',
];
const FILTER_FALLBACK_LABELS = {
    platform: 'Platform', method: 'Signup method', source: 'Source', device_os: 'Device OS',
    app_version: 'App version', build_number: 'Build', os_version: 'OS version',
    manufacturer: 'Manufacturer', model: 'Device model', install_type: 'Install type',
    onboarding_version: 'Onboarding version',
};

/** Read the cohort filters (incl. from / to / exclude_staff) from URL search params. */
export function filtersFromSearchParams(searchParams) {
    const out = {};
    for (const key of [...FILTER_KEYS, 'from', 'to', 'exclude_staff']) {
        const value = searchParams.get(key);
        if (value) out[key] = value;
    }
    return out;
}

export function CohortFilterBar({ filters, onChange, options, labels = FILTER_FALLBACK_LABELS }) {
    const set = (key, value) => {
        const next = { ...filters };
        if (value === '' || value === null || value === undefined || value === false) delete next[key];
        else next[key] = value;
        onChange(next);
    };
    const active = Object.keys(filters).filter((k) => FILTER_KEYS.includes(k) || k === 'from' || k === 'to' || k === 'exclude_staff');
    return (
        <div className={styles.filterBar} aria-label="Cohort filters">
            <div className={styles.filterGrid}>
                {FILTER_KEYS.map((key) => {
                    const opts = options?.[key] || [];
                    const current = filters[key] || '';
                    // Keep the current value selectable even if it has no signups in the window.
                    const values = current && !opts.some((o) => o.value === current) ? [{ value: current, count: 0 }, ...opts] : opts;
                    return (
                        <label key={key} className={styles.filterField}>
                            <span>{labels[key] || key}</span>
                            <select value={current} onChange={(e) => set(key, e.target.value)} disabled={!values.length}>
                                <option value="">All</option>
                                {values.map((o) => (
                                    <option key={o.value} value={o.value}>{o.value} ({o.count})</option>
                                ))}
                            </select>
                        </label>
                    );
                })}
                <label className={styles.filterField}>
                    <span>From (UTC)</span>
                    <input type="date" value={filters.from || ''} onChange={(e) => set('from', e.target.value)} />
                </label>
                <label className={styles.filterField}>
                    <span>To (UTC)</span>
                    <input type="date" value={filters.to || ''} onChange={(e) => set('to', e.target.value)} />
                </label>
            </div>
            <div className={styles.filterFooter}>
                <label className={styles.checkbox}>
                    <input type="checkbox" checked={filters.exclude_staff === 'true'} onChange={(e) => set('exclude_staff', e.target.checked ? 'true' : '')} />
                    Exclude admin / staff accounts
                </label>
                {active.length > 0 && (
                    <button type="button" className={styles.linkBtn} onClick={() => onChange({})}>Clear filters</button>
                )}
                <span className={styles.muted}>Filters combine (AND). Counts in brackets are signups in the selected window. “unknown” = not recorded (e.g. onboarded before diagnostics).</span>
            </div>
        </div>
    );
}

// ─── Formatting ─────────────────────────────────────────────────────────────

// Mobile session replays recorded by Microsoft Clarity (opted-in users only).
// The link opens Clarity's own dashboard, which requires a Clarity login.
export function isClarityUrl(value) {
    if (typeof value !== 'string') return false;
    try {
        const url = new URL(value);
        return url.protocol === 'https:' && url.hostname === 'clarity.microsoft.com';
    } catch {
        return false;
    }
}

function Pct({ value, low }) {
    if (value === null || value === undefined) return <span className={styles.muted}>—</span>;
    return <span className={low ? styles.lowSample : undefined} title={low ? 'Small sample — read the counts, not the percentage' : undefined}>{value}%</span>;
}

function SmallSample({ show }) {
    return show ? <span className={styles.smallTag} title="Fewer than 10 users">small sample</span> : null;
}

const CLASS_STYLE = {
    COMPLETED: styles.clsCompleted,
    LIKELY_TECHNICAL_FAILURE: styles.clsTechnical,
    LIKELY_ABANDONMENT: styles.clsAbandon,
    INTERRUPTED: styles.clsNeutral,
    IN_PROGRESS: styles.clsNeutral,
    NOT_STARTED: styles.clsNeutral,
    UNTRACKED: styles.clsNeutral,
};

// Activation is reported apart from onboarding: completing onboarding is not
// a quiz, a completed quiz or a coin.
const ACTIVATION_LABELS = {
    first_coin: 'First coin earned',
    quiz_completed: 'Quiz completed, no coin yet',
    quiz_started: 'Quiz started, not completed',
    quiz_requested: 'Quiz requested, never started',
    not_activated: 'No quiz requested',
};

export function ActivationMilestones({ activation }) {
    if (!activation) return null;
    const milestones = [
        ['First quiz started', activation.firstQuizStarted, activation.firstQuizStartedAt],
        ['First quiz completed', activation.firstQuizCompleted, activation.firstQuizCompletedAt],
        ['First coin earned', activation.firstCoinEarned, activation.firstCoinEarnedAt],
    ];
    return (
        <div className={styles.journeyDiag} data-testid="activation-milestones">
            <strong>Activation:</strong>{' '}
            {milestones.map(([label, reached, at]) => (
                <span key={label} className={styles.pill} title={at ? new Date(at).toLocaleString() : undefined}>
                    {reached ? '✓' : '✗'} {label}
                </span>
            ))}
            {Number.isFinite(activation.quizStartRequests) && (
                <span className={styles.sub}>
                    {' '}— {activation.quizStartRequests} start request{activation.quizStartRequests === 1 ? '' : 's'}
                    {activation.quizStartsBlocked ? `, ${activation.quizStartsBlocked} blocked by a gate (expected)` : ''}
                    {activation.technicalStartFailures ? `, ${activation.technicalStartFailures} technical failure${activation.technicalStartFailures === 1 ? '' : 's'}` : ''}
                </span>
            )}
        </div>
    );
}

const REASON_LABELS = {
    completed: 'Completed',
    no_onboarding_events: 'No onboarding activity',
    no_step_events: 'App build without step diagnostics',
    recent_activity: 'Active in the last hour',
    blocking_error: 'Error on the step where they stopped',
    attempt_without_success: 'Tapped the CTA; the step never succeeded',
    unclean_exit: 'App ended while in the foreground (possible crash / force close)',
    left_via_back: 'Left onboarding with Back',
    no_cta_attempt: 'Viewed the step, never tapped its CTA',
    no_cta_attempt_after_nonblocking_error: 'Never tapped the CTA (a non-blocking error was seen)',
    stopped_between_steps: 'Finished a step, never reached the next',
    insufficient_evidence: 'Not enough evidence',
};

// ─── Step funnel ────────────────────────────────────────────────────────────

export function OnboardingStepFunnel({ data, onDrill }) {
    const { steps, cohort, minSample } = data;
    return (
        <div className={styles.tableWrap}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>Step</th>
                        <th className={styles.num}>Reached</th>
                        <th className={styles.num}>CTA tapped</th>
                        <th className={styles.num}>Succeeded</th>
                        <th className={styles.num}>Step conv.</th>
                        <th className={styles.num}>Users w/ errors</th>
                        <th className={styles.num}>Stopped here</th>
                        <th>Why they stopped (diagnostic)</th>
                    </tr>
                </thead>
                <tbody>
                    <tr className={styles.anchorRow}>
                        <td>Onboarding started</td>
                        <td className={styles.num}>{cohort.instrumentedStarters}</td>
                        <td colSpan={6} className={styles.muted}>
                            users with step-level data · {cohort.starters} starters in total ({cohort.starters - cohort.instrumentedStarters} on app builds without diagnostics)
                        </td>
                    </tr>
                    {steps.map((s) => (
                        <tr key={s.key} className={s.preOnboarding || s.postCompletion ? styles.sideRow : undefined}>
                            <td>
                                <div className={styles.stepLabel}>{s.number}. {s.label}</div>
                                {s.note && <div className={styles.stepNote}>{s.note}</div>}
                            </td>
                            <td className={styles.num}>
                                {s.reached} <span className={styles.sub}>(<Pct value={s.reachedPct} low={s.lowSample} /> of {s.reachedPctOf})</span>
                            </td>
                            <td className={styles.num}>
                                {s.attempted} <span className={styles.sub}>(<Pct value={s.attemptRate} low={s.lowSample} />)</span>
                                {s.blockedTaps > 0 && <div className={styles.sub} title="Tapped the CTA while it was still disabled">+{s.blockedTaps} disabled taps</div>}
                            </td>
                            <td className={styles.num}>
                                {s.completed}
                                {s.skippable && s.skipped > 0 && <div className={styles.sub} title="Optional step: Skip for now is a way through it, not a drop-off">+{s.skipped} skipped</div>}
                                {s.autoSkipped > 0 && <div className={styles.sub} title="Bypassed automatically (nothing to show)">{s.autoSkipped} bypassed automatically</div>}
                            </td>
                            <td className={styles.num}><Pct value={s.stepConversion} low={s.lowSample} /></td>
                            <td className={`${styles.num} ${s.usersWithErrors ? styles.errText : ''}`}>
                                {s.usersWithErrors}{s.errorEvents > s.usersWithErrors && <span className={styles.sub}> ({s.errorEvents} events)</span>}
                            </td>
                            <td className={styles.num}>
                                {s.stoppedHere > 0 ? (
                                    <button type="button" className={styles.linkBtn} onClick={() => onDrill?.({ step: s.key })}>{s.stoppedHere}</button>
                                ) : 0}
                                {s.stoppedHerePct !== null && s.stoppedHere > 0 && <span className={styles.sub}> (<Pct value={s.stoppedHerePct} low={s.lowSample} />)</span>}
                            </td>
                            <td>
                                <StoppedBy stoppedBy={s.stoppedBy} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <p className={styles.footnote}>
                Reached = viewed the step. CTA tapped = tried to continue (Get started / Continue / Finish / Skip / Earn coin).
                Succeeded = the step’s action worked. High “CTA tapped” with low “Succeeded” points to a technical problem;
                low “CTA tapped” with no errors points to abandonment. On an optional step (title selection) a skip counts
                towards step conversion — it is never a drop-off. Rates under {minSample} users are greyed.
            </p>
        </div>
    );
}

function StoppedBy({ stoppedBy = {} }) {
    const entries = Object.entries(stoppedBy).sort((a, b) => b[1] - a[1]);
    if (!entries.length) return <span className={styles.muted}>—</span>;
    return (
        <span className={styles.pills}>
            {entries.map(([key, n]) => (
                <span key={key} className={`${styles.pill} ${CLASS_STYLE[key] || ''}`}>{n} {key.replace(/^LIKELY_/, '').replace(/_/g, ' ').toLowerCase()}</span>
            ))}
        </span>
    );
}

// ─── Classification / headline ──────────────────────────────────────────────

export function ClassificationSummary({ classifications, onDrill }) {
    return (
        <div className={styles.classGrid}>
            {classifications.map((c) => (
                <button key={c.key} type="button" className={`${styles.classCard} ${CLASS_STYLE[c.key] || ''}`} onClick={() => onDrill?.({ classification: c.key })} disabled={!c.count}>
                    <span className={styles.classCount}>{c.count}</span>
                    <span className={styles.classLabel}>{c.label}</span>
                    {c.pctOfStarters !== null && <span className={styles.sub}>{c.pctOfStarters ?? 0}% of starters</span>}
                </button>
            ))}
        </div>
    );
}

export function DiagnosticSummaryCards({ summary, trackedFrom }) {
    const p = summary.byPlatform || {};
    const card = (label, value, hint, low) => (
        <div className={styles.statCard} key={label}>
            <div className={styles.statLabel}>{label}</div>
            <div className={`${styles.statValue} ${low ? styles.lowSample : ''}`}>{value}</div>
            {hint && <div className={styles.statHint}>{hint}</div>}
        </div>
    );
    const rate = (r) => (r === null || r === undefined ? '—' : `${r}%`);
    const latest = summary.latestBuild?.android;
    return (
        <div className={styles.statsGrid}>
            {card('Biggest drop-off step',
                summary.biggestDropOff ? summary.biggestDropOff.label : '—',
                summary.biggestDropOff ? `${summary.biggestDropOff.stoppedHere} users (${rate(summary.biggestDropOff.stoppedHerePct)} of starters with step data) stop here` : (trackedFrom ? 'No drop-offs in this window' : 'Waiting for step-level data'))}
            {card('Likely technical failure', rate(summary.technicalFailureRate), `${summary.likelyTechnical} of ${summary.starters} starters`, summary.lowSample)}
            {card('Likely abandonment', rate(summary.abandonmentRate), `${summary.likelyAbandonment} of ${summary.starters} starters`, summary.lowSample)}
            {card('Android completion', rate(p.android?.completionRate), `${p.android?.completions ?? 0} of ${p.android?.starters ?? 0} starters`, p.android?.lowSample)}
            {card('Web completion', rate(p.web?.completionRate), `${p.web?.completions ?? 0} of ${p.web?.starters ?? 0} starters`, p.web?.lowSample)}
            {card('iOS completion', rate(p.ios?.completionRate), `${p.ios?.completions ?? 0} of ${p.ios?.starters ?? 0} starters`, p.ios?.lowSample)}
            {card('Latest Android build', latest ? rate(latest.completionRate) : '—',
                latest ? `${latest.appVersion || '?'} (${latest.build}) · ${latest.completions} of ${latest.starters} starters` : 'No build data yet', latest?.lowSample)}
        </div>
    );
}

// ─── Errors / devices / quiz handoff ────────────────────────────────────────

export function ErrorSignatures({ errors }) {
    if (!errors.length) return <div className={styles.empty}>No technical errors recorded for this cohort.</div>;
    return (
        <div className={styles.tableWrap}>
            <table className={styles.table}>
                <thead>
                    <tr><th>Step</th><th>Operation</th><th>Type</th><th className={styles.num}>HTTP</th><th>Code</th><th className={styles.num}>Users</th><th className={styles.num}>Events</th><th>Platforms</th><th>Last seen</th></tr>
                </thead>
                <tbody>
                    {errors.map((e) => (
                        <tr key={`${e.step}|${e.operation}|${e.errorType}|${e.httpStatus}|${e.errorCode}`}>
                            <td>{e.step}</td>
                            <td>{e.operation || '—'}{!e.blocking && <span className={styles.smallTag}>non-blocking</span>}</td>
                            <td>{e.errorType}</td>
                            <td className={styles.num}>{e.httpStatus ?? '—'}</td>
                            <td>{e.errorCode || '—'}</td>
                            <td className={styles.num}>{e.users}</td>
                            <td className={styles.num}>{e.events}</td>
                            <td>{e.platforms.join(', ') || '—'}</td>
                            <td>{e.lastAt ? new Date(e.lastAt).toLocaleString() : '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function BreakdownTable({ title, rows, minSample }) {
    return (
        <div className={styles.breakdown}>
            <h3 className={styles.subTitle}>{title}</h3>
            {!rows.length ? <div className={styles.empty}>No data.</div> : (
                <table className={styles.table}>
                    <thead>
                        <tr><th>Segment</th><th className={styles.num}>Starters</th><th className={styles.num}>Completed</th><th className={styles.num}>Rate</th><th className={styles.num}>W/ errors</th><th className={styles.num}>Likely tech.</th></tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.segment}>
                                <td>{r.segment}<SmallSample show={r.starters > 0 && r.starters < minSample} /></td>
                                <td className={styles.num}>{r.starters}</td>
                                <td className={styles.num}>{r.completions}</td>
                                <td className={styles.num}><Pct value={r.completionRate} low={r.lowSample} /></td>
                                <td className={`${styles.num} ${r.usersWithErrors ? styles.errText : ''}`}>{r.usersWithErrors}</td>
                                <td className={styles.num}>{r.likelyTechnical}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export function AndroidDeviceBreakdown({ devices, minSample }) {
    if (!devices.androidSignups) return <div className={styles.empty}>No Android signups in this cohort.</div>;
    return (
        <div className={styles.breakdownGrid}>
            <BreakdownTable title="By manufacturer" rows={devices.byManufacturer} minSample={minSample} />
            <BreakdownTable title="By app version (build)" rows={devices.byAppBuild} minSample={minSample} />
            <BreakdownTable title="By Android version" rows={devices.byOsVersion} minSample={minSample} />
            <BreakdownTable title="By install type" rows={devices.byInstallType} minSample={minSample} />
            <BreakdownTable title="By model (top 25)" rows={devices.byModel} minSample={minSample} />
        </div>
    );
}

export function QuizHandoff({ handoff }) {
    return (
        <div className={styles.columns2}>
            <table className={styles.table}>
                <thead><tr><th>Step</th><th className={styles.num}>Users</th><th className={styles.num}>From previous</th></tr></thead>
                <tbody>
                    {handoff.steps.map((s) => (
                        <tr key={s.key}><td>{s.label}</td><td className={styles.num}>{s.count}</td><td className={styles.num}><Pct value={s.conversionFromPrevious} low={handoff.lowSample} /></td></tr>
                    ))}
                </tbody>
            </table>
            <div>
                <table className={styles.table}>
                    <thead><tr><th>Among users who completed onboarding</th><th className={styles.num}>Users</th></tr></thead>
                    <tbody>
                        {handoff.problems.map((p) => <tr key={p.key}><td>{p.label}</td><td className={styles.num}>{p.users}</td></tr>)}
                    </tbody>
                </table>
                {handoff.coinStepAutoSkipped > 0 && (
                    <p className={styles.footnote}>
                        {handoff.coinStepAutoSkipped} completed onboarding without picking a title, so the coin step was bypassed automatically (not counted as a problem).
                    </p>
                )}
                {handoff.startBlockReasons?.length > 0 && (
                    <p className={styles.footnote}>
                        Quiz starts blocked by a gate (expected, not failures): {handoff.startBlockReasons.map((r) => `${r.reason} × ${r.events}`).join(' · ')}
                    </p>
                )}
                {handoff.startFailureReasons.length > 0 && (
                    <p className={styles.footnote}>
                        Quiz start failures: {handoff.startFailureReasons.map((r) => `${r.reason} × ${r.events}${r.technical ? '' : ' (gate)'}`).join(' · ')}
                    </p>
                )}
            </div>
        </div>
    );
}

// ─── Title selection (optional since onboarding v2) ────────────────────────

const SKIP_REASON_LABELS = {
    no_selected_movies: 'no titles picked',
    no_eligible_movies: 'no picked title could start a challenge',
};

export function TitleSelectionOutcomes({ outcomes, onDrill }) {
    if (!outcomes || !outcomes.reached) return <div className={styles.empty}>No users with step-level data reached title selection in this cohort.</div>;
    const card = (label, value, hint, low) => (
        <div className={styles.statCard} key={label}>
            <div className={styles.statLabel}>{label}</div>
            <div className={`${styles.statValue} ${low ? styles.lowSample : ''}`}>{value}</div>
            {hint && <div className={styles.statHint}>{hint}</div>}
        </div>
    );
    const rate = (r) => (r === null || r === undefined ? '—' : `${r}%`);
    const bucket = (b) => outcomes.selectionCounts.find((c) => c.bucket === b)?.users ?? 0;
    return (
        <>
            <div className={styles.statsGrid}>
                {card('Completed with picks', outcomes.completed, `${rate(outcomes.completedPct)} of ${outcomes.reached} who reached the step`, outcomes.lowSample)}
                {card('Picked 1', bucket('1'))}
                {card('Picked 2', bucket('2'))}
                {card('Picked 3+', bucket('3+'), bucket('unknown') ? `+${bucket('unknown')} with no count recorded` : undefined)}
                {card('Skipped on purpose', outcomes.skipped, `${rate(outcomes.skippedPct)} · ${outcomes.skippedThenCompleted} went on to finish onboarding`, outcomes.lowSample)}
                {card('Abandoned title selection', outcomes.abandoned, `${rate(outcomes.abandonedPct)} · ${outcomes.stoppedHere} stopped here in total`, outcomes.lowSample)}
                {card('Coin step bypassed automatically', outcomes.coinStepAutoSkipped,
                    outcomes.coinStepAutoSkipReasons.map((r) => `${r.users} ${SKIP_REASON_LABELS[r.reason] || r.reason}`).join(' · ') || 'nothing picked → nothing to offer')}
                {card('Completed onboarding', outcomes.completedOnboarding, `of ${outcomes.reached} who reached title selection`)}
            </div>
            {outcomes.stoppedHere > 0 && (
                <p className={styles.footnote}>
                    Stopped on title selection: <StoppedBy stoppedBy={outcomes.stoppedBy} />{' '}
                    <button type="button" className={styles.linkBtn} onClick={() => onDrill?.({ step: 'titles' })}>list users</button>
                </p>
            )}
            {outcomes.byOnboardingVersion.length > 0 && (
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr><th>Onboarding version</th><th className={styles.num}>Reached</th><th className={styles.num}>Continued with picks</th><th className={styles.num}>Skipped</th><th className={styles.num}>Completed onboarding</th></tr>
                        </thead>
                        <tbody>
                            {outcomes.byOnboardingVersion.map((v) => (
                                <tr key={v.version}>
                                    <td>{v.version === '1' ? '1 (3 picks required)' : v.version === '2' ? '2 (optional)' : v.version}</td>
                                    <td className={styles.num}>{v.reached}</td>
                                    <td className={styles.num}>{v.completed}</td>
                                    <td className={styles.num}>{v.version === '1' ? <span className={styles.muted}>n/a</span> : v.skipped}</td>
                                    <td className={styles.num}>{v.completedOnboarding}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p className={styles.footnote}>
                Users on onboarding version 1 had to pick 3 titles; their events are never counted as skips. “Abandoned” uses the same
                heuristic as the classification above: viewed the step and never tapped Continue or Skip, with no blocking error.
            </p>
        </>
    );
}

// ─── Drill-down list + journey ──────────────────────────────────────────────

export function DiagnosticUsersList({ filters, range, drill, onClose, onOpenUser }) {
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => { setPage(1); }, [drill]);
    useEffect(() => {
        let cancelled = false;
        setError(null);
        adminApi.listOnboardingDiagnosticUsers({ range, ...filters, ...drill, page })
            .then((res) => { if (!cancelled) setData(res); })
            .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load users'); });
        return () => { cancelled = true; };
    }, [filters, range, drill, page]);

    const title = drill.classification ? drill.classification.replace(/_/g, ' ').toLowerCase() : `stopped at ${drill.step}`;
    const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
    return (
        <div className={styles.drill}>
            <div className={styles.drillHeader}>
                <h3 className={styles.subTitle}>Users: {title}{data ? ` (${data.total})` : ''}</h3>
                <button type="button" className={styles.linkBtn} onClick={onClose}>Close</button>
            </div>
            {error && <div className={styles.errText}>{error}</div>}
            {data && (
                <>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead><tr><th>User</th><th>Signed up</th><th>Platform / method</th><th>Build</th><th>Device</th><th>Install</th><th>Stopped at</th><th>Diagnosis</th><th>Activation</th><th className={styles.num}>Errors</th></tr></thead>
                            <tbody>
                                {data.users.map((u) => (
                                    <tr key={u.id}>
                                        <td><button type="button" className={styles.linkBtn} onClick={() => onOpenUser(u.id)}>@{u.username || u.id.slice(0, 8)}</button></td>
                                        <td>{new Date(u.signedUpAt).toLocaleDateString()}</td>
                                        <td>{u.platform || '?'} / {u.method || '?'}</td>
                                        <td>{u.appVersion || u.buildNumber ? `${u.appVersion || '?'} (${u.buildNumber || '?'})` : '—'}</td>
                                        <td>{u.device || '—'}{u.osVersion ? ` · ${u.osVersion}` : ''}</td>
                                        <td>{u.installType || '—'}</td>
                                        <td>{u.stepLabel || '—'}</td>
                                        <td><span className={`${styles.pill} ${CLASS_STYLE[u.classification] || ''}`}>{u.classificationLabel}</span> <span className={styles.sub}>{REASON_LABELS[u.reason] || u.reason}</span></td>
                                        <td>{u.activation ? ACTIVATION_LABELS[u.activation.status] || u.activation.status : '—'}</td>
                                        <td className={styles.num}>{u.errorCount}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {pages > 1 && (
                        <div className={styles.pager}>
                            <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
                            <span>Page {page} of {pages}</span>
                            <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export function OnboardingJourney({ userId }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        let cancelled = false;
        setData(null);
        setError(null);
        adminApi.getUserOnboardingJourney(userId)
            .then((res) => { if (!cancelled) setData(res); })
            .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load the onboarding journey'); });
        return () => { cancelled = true; };
    }, [userId]);

    if (error) return <div className={styles.errText}>{error}</div>;
    if (!data) return <div className={styles.muted}>Loading onboarding journey…</div>;
    const { user, context, classification, activation, events, truncated } = data;
    const facts = [
        user.signupPlatform || context?.platform, user.signupMethod,
        context?.appVersion || context?.buildNumber ? `App ${context.appVersion || '?'} (${context.buildNumber || '?'})` : null,
        [context?.deviceManufacturer, context?.deviceModel].filter(Boolean).join(' ') || null,
        context?.osVersion ? `OS ${context.osVersion}` : null,
        context?.installType ? `${context.installType} install${context.firstInstalledVersion ? ` (first ${context.firstInstalledVersion})` : ''}` : null,
        context?.screen, context?.onboardingVersion ? `onboarding v${context.onboardingVersion}` : null,
        user.source ? `source ${user.source}` : null,
    ].filter(Boolean);
    return (
        <div className={styles.journey}>
            <div className={styles.journeyFacts}>{facts.join(' · ') || 'No device / build data recorded'}</div>
            <div className={styles.journeyDiag}>
                <span className={`${styles.pill} ${CLASS_STYLE[classification.key] || ''}`}>{classification.label}</span>
                {classification.stepLabel && <span> at <strong>{classification.stepLabel}</strong></span>}
                <span className={styles.sub}> — {REASON_LABELS[classification.reason] || classification.reason}. Diagnostic heuristic, not a verdict.</span>
            </div>
            <ActivationMilestones activation={activation} />
            {!events.length ? <div className={styles.muted}>No activation events recorded for this user.</div> : (
                <ol className={styles.timeline}>
                    {events.map((e, i) => (
                        <li key={i} className={`${styles.tlItem} ${styles[`sev_${e.severity}`] || ''}`}>
                            <span className={styles.tlTime}>{new Date(e.at).toLocaleString()}</span>
                            <span className={styles.tlLabel}>{e.label}</span>
                            {Object.keys(e.details).length > 0 && (
                                <span className={styles.tlDetails}>
                                    {Object.entries(e.details).map(([k, v]) => `${k}=${v}`).join(' · ')}
                                </span>
                            )}
                            {e.platform && <span className={styles.tlMeta}>{e.platform}{e.sessionId ? ` · session ${e.sessionId}` : ''}</span>}
                            {isClarityUrl(e.clarityUrl) && !events.slice(0, i).some((prev) => prev.clarityUrl === e.clarityUrl) && (
                                <a className={styles.tlMeta} href={e.clarityUrl} target="_blank" rel="noopener noreferrer">View in Clarity ↗</a>
                            )}
                        </li>
                    ))}
                </ol>
            )}
            {truncated && <div className={styles.muted}>Showing the first 500 events.</div>}
        </div>
    );
}
